<template>
  <component
    :is="asForm ? BaseForm : 'div'"
    ref="attributeSelectionFormRef"
    :form="asForm ? attributeSelection : undefined"
  >
    <div :class="brandClasses.variantSelectionWrapper">
      <section
        v-for="({ groupBy, groupedOptions, isSingleton, label, type }, i) in variantAttributes"
        :key="type"
        :data-scroll-el="`${type}Pickers`"
        :data-test-id="`${type}-groups`"
      >
        <div
          v-if="!(isDummyCustoms && type === 'color')"
          :class="{
            'flex items-center space-x-2': showBadge,
            'hidden': !showSingleAttributes && type !== 'color' && isSingleton,
            'sr-only': isOutletOnlyColor(type, groupedOptions),
          }"
        >
          <div v-if="showBadge" class="h-4 w-4 flex justify-center rounded-full bg-grey-10">
            <span class="text-xs c-white fw-bold">{{ i + 1 }}</span>
          </div>
          <div :class="brandClasses.variantSelectionLabel">
            <div class="flex gap-1">
              <h2 :id="label" class="fw-bold">
                <span>
                  {{ isDigitalProduct && type === 'size' ? $t.amount : label }}
                </span>
                <span v-if="type === 'size' && product?.sizingStandard">
                  ({{ product?.sizingStandard }})
                </span>

                <span v-if="type === 'size' && selectedConvertedSizeLabel">
                  ({{ selectedConvertedSizeLabel }})
                </span>
                <span aria-hidden="true"> : </span>
              </h2>
              {{ isActiveInStandardColorGroup(type, groupedOptions) ? attributeSelectionLabels[type]?.value : '' }}
              <template v-if="type !== 'color' && showStockStatusLabel">
                - <span :class="brandClasses.variantSelectionStockStatus">{{ selectedVariant?.stockStatusLabel }}</span>
              </template>
            </div>
          </div>
        </div>
        <vf-form-error v-if="type !== 'color' && error && !attributeSelection[type]" class="mt-1">
          {{ isDigitalProduct ? $t.selectCardAmountError : replaceAll($t.selectSizeError, { label }) }}
        </vf-form-error>
        <template v-if="!(isDummyCustoms && type === 'color')">
          <div
            v-for="({ groupValue, options }, j) in groupedOptions"
            :key="j"
            :class="[
              brandClasses.variantSelectionOptions,
              j !== groupedOptions.length - 1 ? brandClasses.variantSelectionGroupedOptionsGreaterThanOne : '',
              { 'o:hidden': !showSingleAttributes && type !== 'color' && isSingleton },
            ]"
            :data-test-id="`${type}-groups-item`"
          >
            <product-pricing
              v-if="groupValue && typeof groupValue === 'object' && groupedOptions.length > 1 && groupBy === 'price'"
              :class="brandClasses.variantSelectionGroupedOptionsPrice"
              :currency="product.currency"
              :price="groupValue as PriceFull"
            />
            <p
              v-if="type === 'color' && groupValue === true"
              class="mb-4 fw-bold"
            >
              {{ $t.outletColor }}<template v-if="isActiveColorInOptions(options)">
                : {{ attributeSelectionLabels[type]?.value }}
              </template>
            </p>
            <component
              :is="options.length > swatches.collapseThreshold ? BaseCollapse : 'div'"
              v-if="type === 'color'"
              v-bind="options.length > swatches.collapseThreshold && {
                classToggle: 'self-end',
                classContent: isRectangle ? 'max-h-48' : 'max-h-54 lg:max-h-75',
                disableVisibilityToggle: true,
                inverted: true,
              }"
            >
              <fieldset :aria-labelledby="label" class="flex wrap" :class="{ 'gap-1': isRectangle }">
                <vf-color-picker
                  v-for="{ available, id, label: optionLabel, swatch, url, value } in options"
                  :key="optionLabel"
                  :active="!getIsPending(value, id, pending) && getIsActive(value, id)"
                  :color="swatch?.color"
                  :loading="getIsPending(value, id, pending)"
                  :name="optionLabel"
                  :size="colorPickerSize"
                  :thumbnail="swatch?.image"
                  :unavailable="!isComingSoon && (!available && !getIsPending(value, id, pending))"
                  :variant="colorPickerShape"
                  @click="selectNewColor(enableColorAsAttribute ? value : id, url, optionLabel)"
                />
              </fieldset>
              <!-- toggle for base-collapse -->
              <template #toggle="{ expanded }">
                <div>
                  <p class="py-2 text-right text-sm lh-3 underlined lg:py-4">
                    {{ expanded ? $t.viewLess : $t.viewMore }}
                  </p>
                  <vf-icon
                    v-if="swatches.showCollapseIcon"
                    class="ml-1"
                    :dir="expanded ? 'up' : 'down'"
                    name="chevron"
                    size="md"
                  />
                </div>
              </template>
            </component>
            <fieldset v-else :aria-labelledby="label">
              <base-form-field v-slot="{ attrs }" :name="type" :rule="validateRequired()" :selector="`${type}Pickers`">
                <div class="flex gap-2 wrap">
                  <vf-size-picker
                    v-for="({ available, label: optionLabel, id, value, productUrl }, n) in options"
                    :key="optionLabel"
                    :class="{ 'pointer-events-none': !allowOutOfStockSelection && !getAttrOptionAvailable(type, value) && !getAttrOptionNotifyMe(type, value) }"
                    :model-value="attributeSelection[type]"
                    :name="label"
                    :required="n === 0"
                    :type
                    :unavailable="isSplitVariant(productUrl)
                      && !getIsPending(value, id, pending)
                      && (!available || !getAttrOptionAvailable(type, value))"
                    :value
                    v-bind="attrs"
                    @input="isSplitVariant(productUrl) && $emit('selectSize', type, value, optionLabel)"
                  >
                    <template v-if="isSplitVariant(productUrl)">
                      {{ isDigitalProduct && !Number.isNaN(Number.parseInt(optionLabel))
                        ? $formatPrice(Number.parseInt(optionLabel),
                                       product.currency,
                                       { trailingZeroDisplay: 'stripIfInteger' })
                        : optionLabel }}
                    </template>
                    <base-link
                      v-else
                      :to="{
                        path: productUrl,
                        state: { ...history.state, skuChange: true },
                        query: { [type]: value },
                      }"
                      @click="selectExtendedSize(label, optionLabel)"
                    >
                      {{ optionLabel }}
                      <span class="absolute-0" />
                    </base-link>
                  </vf-size-picker>
                </div>
              </base-form-field>
            </fieldset>
            <slot v-if="type === targetAttributeTypeForSizing" name="sizing-info" />
          </div>
        </template>
        <vf-link
          v-if="$feature.showCustomizeOnPDP && product.customsExternalUrl && type === 'color'"
          class="items-center gap-1 text-sm fw-bold lh-3 i-flex hover:c-brand-3"
          :class="{ 'mt-4': !isDummyCustoms }"
          :to="product.customsExternalUrl"
          @click="$gtm.push('pdpPage.onCustomize')"
        >
          <vf-icon name="customs" size="md" /> {{ $t.customize }}
        </vf-link>
        <div v-if="isComingSoon" class="mt-4 space-y-4">
          <p class="title-4 c-grey-20">
            {{ comingSoonText }}
          </p>
          <p v-if="selectedVariant?.notifyMe">
            {{ replaceAll($t.comingSoonDescription, { productName: product.name }) }}
          </p>
        </div>
      </section>
    </div>
  </component>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { Responsive } from '#types/common'
import type {
  AttributeSelection,
  ColorOption,
  ColorSwatchShape,
  OptionGroups,
  PriceFull,
  ProductAttributeSelectionLabels,
  ProductAttributeType,
  ProductExtended,
  SelectedVariant
} from '#types/product'
import type { SizesSubUnion } from '#types/sizes'
import { BaseCollapse, BaseForm } from '#components'

export type Props = {
  /*
  * variants, productInventory props are added only for consistency with other brands
  * They aren't used in the component
  * but allows to avoid situations with incorrect props resolution when rendering the DOM
  */
  attributeSelectionLabels: ProductAttributeSelectionLabels
  colorSize?: SizesSubUnion<'sm' | 'md' | 'lg'> | Responsive<SizesSubUnion<'sm' | 'md' | 'lg'>>
  colorSwatch?: ColorSwatchShape
  error?: boolean
  hideStockStatusLabel?: boolean
  isComingSoon?: boolean
  isDigitalProduct?: boolean
  getAttrOptionAvailable?: (type: ProductAttributeType, value: string) => boolean | undefined
  getAttrOptionNotifyMe?: (type: ProductAttributeType, value: string) => boolean | undefined
  pending?: boolean
  product: ProductExtended
  selectedVariant?: SelectedVariant
  showBadge?: boolean
  showSingleAttributes?: boolean
  asForm?: boolean
  variants?: any
}

export type Emits = {
  selectColor: [id: string, url: string, optionLabel: string]
  selectSize: [type: Exclude<ProductAttributeType, 'color'>, value: string, label: string]
}

const {
  colorSize,
  colorSwatch,
  hideStockStatusLabel,
  isComingSoon,
  getAttrOptionAvailable = () => true,
  getAttrOptionNotifyMe = () => true,
  product,
  selectedVariant,
  showSingleAttributes = true
} = defineProps<Props>()

const emit = defineEmits<Emits>()

const { brandClasses, enableColorAsAttribute, swatches, allowOutOfStockSelection } = useAppConfig().pages.pdp || {}
const { $feature, $gtm, $t } = useNuxtApp()
const route = useRoute()
const { getComingSoonText } = useComingSoon()
const { history } = useRouter().options
const attributeSelectionFormRef = defineModel<InstanceType<typeof BaseFormType> | null>('formRef', { required: false })

const colorPickerShape = colorSwatch ?? swatches.variant
const colorPickerSize = colorSize ?? swatches.size
const isRectangle = colorPickerShape === 'rectangle'
const attributeSelection = defineModel<AttributeSelection>({ required: true })

const selectedProductId = ref()

const variantAttributes = computed(() =>
  isComingSoon ? product.attributes.filter((attribute) => attribute.type === 'color') : product.attributes)

const targetAttributeTypeForSizing = computed(() => {
  const nonColorAttributes = variantAttributes.value
    .filter(({ type }) => type !== 'color')
    .filter(({ options }) => options.length > (!showSingleAttributes ? 1 : 0))

  return nonColorAttributes.length > 1
    ? nonColorAttributes[1]?.type
    : nonColorAttributes[0]?.type
})

const selectedConvertedSizeLabel = computed(() => {
  if (!attributeSelection.value?.size || !product?.variants) return

  const matchedVariant = product.variants.find(
    (variant) => variant.attributes?.size === attributeSelection.value.size
  )

  return matchedVariant?.convertedSizeLabel
})

const isCurrentUrl = (productUrl?: string) => productUrl === route.path
const isSplitVariant = (productUrl?: string) => !productUrl || isCurrentUrl(productUrl)

const selectNewColor = (id, url, optionLabel) => {
  selectedProductId.value = id
  emit('selectColor', id, url, optionLabel)
}

const getIsActive = (value: string, id: string) =>
  enableColorAsAttribute ? attributeSelection.value.color === value : product.id === id

const isActiveColorInOptions = (options: ColorOption[]) =>
  options.some(({ id, value }) => getIsActive(value, id))

const isOutletOnlyColor = (attrType: ProductAttributeType, groupedOptions: OptionGroups[]) =>
  attrType === 'color' && groupedOptions.length === 1 && groupedOptions[0].groupValue === true

const isActiveInStandardColorGroup = (attrType: ProductAttributeType, groupedOptions: OptionGroups[]) => {
  const standardGroup = groupedOptions.find(({ groupValue }) => groupValue === false)

  return attrType !== 'color' || !standardGroup || isActiveColorInOptions(standardGroup.options)
}

const getIsPending = (value: string, id: string, pending?: boolean) => {
  if (!pending) return

  return enableColorAsAttribute
    ? (attributeSelection.value.color === value && id !== product.id)
    : selectedProductId.value === id
}

const selectExtendedSize = (attrName: string, label: string) => {
  $gtm.push('pdpPage.onSelectSize', `${product.id} - ${label}`, attrName)
}

const isDummyCustoms = computed(() =>
  product.attributes.find(({ type }) => type === 'color')?.options?.some(({ label }) => label === 'Customs')
)

const comingSoonText = computed(() => getComingSoonText(product.targetPublishDate))

const showStockStatusLabel = computed<boolean>(() =>
  $feature.showSizeLevelStockMessagingOnPDP && !!selectedVariant?.stockStatusLabel && !hideStockStatusLabel)

defineExpose({
  validate: () => attributeSelectionFormRef.value?.validate()
})
</script>
